import { useEffect, useMemo, useRef, useState } from "react"
import { useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import {
    Alert, Box, Button, IconButton, InputAdornment, Paper, Stack,
    TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import ViewListIcon from "@mui/icons-material/ViewList"
import GridViewIcon from "@mui/icons-material/GridView"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import CloseIcon from "@mui/icons-material/Close"
import { alpha, ThemeProvider, useTheme } from "@mui/material/styles"

import { useParams, useSearchParams } from "react-router-dom"
import { APIError } from "../../app/appTypes"
import { ErrorContainer } from "../../app/ErrorContainer"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { LoadingContainer } from "../../app/LoadingContainer"
import { ReportPrinter } from "../common/ReportPrinter"
import { selectSettings } from "../settings/settingsSlice"
import { selectLoggedUser } from "../user/userSlice"
import { showError, showSuccess } from "../notifications/notificationsSlice"
import { CloseMeetingDialog } from "../project/mettingMinutes/dialogs/CloseMeetingDialog"
import { OperationalDashboardMeetingPanel } from "./components/OperationalDashboardMeetingPanel"
import { AreaInspector, AreaListItem } from "./components/OperationalDashboardAreaList"
import { AreaPanel, topicSearchText } from "./components/OperationalDashboardAreaPanel"
import type { AreaRatingValue } from "./components/AreaRatingSelector"
import type { View } from "./components/OperationalDashboardAreaPanel"
import { OverviewColorMode, OverviewColorModeMenu } from "./components/OverviewColorModeMenu"
import { createOverviewTheme } from "./components/overviewTheme"
import { OperationalDashboardAreaDialog } from "./dialogs/OperationalDashboardAreaDialog"
import { OperationalDashboardContentDialog } from "./dialogs/OperationalDashboardContentDialog"
import { OperationalDashboardHistoryDialog, OperationalDashboardHistoryTarget } from "./dialogs/OperationalDashboardHistoryDialog"
import { OperationalDashboardAgendaSummaryDialog } from "./dialogs/OperationalDashboardAgendaSummaryDialog"
import {
    getOperationalDashboardOverviewSettings, loadOperationalDashboardAreaConfiguration, loadOperationalDashboardOverview,
    isMeetingMinuteContent, OperationalDashboardAreaOverviewDto, OperationalDashboardOverviewDto,
    OperationalDashboardAreaContentOverviewDto, updateOperationalDashboardAgendaDiscussionStatus,
    updateOperationalDashboardAreaAgendaRating,
    updateOperationalDashboardAreaContentPriority,
} from "./operationalDashboards"
import type { UpdateOperationalDashboardAreaContentPriorityRequest } from "./operationalDashboards"

const showDiscussionStatus = false

const OperationalDashboardOverviewContent = ({ colorMode, onColorModeChange }: {
    colorMode: OverviewColorMode
    onColorModeChange: (mode: OverviewColorMode) => void
}) => {
    const { id } = useParams()
    const [searchParams] = useSearchParams()
    const meetingIdParam = searchParams.get("meetingId")
    const dashboardId = Number(id)
    const meetingId = meetingIdParam === null ? undefined : Number(meetingIdParam)
    const validId = id !== undefined && Number.isInteger(dashboardId) && dashboardId > 0
    const validMeetingId = meetingId === undefined || Number.isInteger(meetingId) && meetingId > 0
    const dispatch = useAppDispatch()
    const loggedUser = useAppSelector(selectLoggedUser)
    const { locale } = useAppSelector(selectSettings)
    const { l10n } = useLocalization()
    const label = (key: string, fallback: string) => l10n.getString(`operational-dashboard-${key}`, null, `_${fallback}`)
    const [data, setData] = useState<OperationalDashboardOverviewDto | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<APIError | null>(null)
    const [reload, setReload] = useState(0)
    const [view, setView] = useState<View>("tiles")
    const [expandedAreaIds, setExpandedAreaIds] = useState<Set<number>>(() => new Set())
    const expandedFor = useRef<string | null>(null)
    const [editMode, setEditMode] = useState(false)
    const [meetingDetailsOpen, setMeetingDetailsOpen] = useState(false)
    const [printDialogOpen, setPrintDialogOpen] = useState(false)
    const [closeMeetingDialogOpen, setCloseMeetingDialogOpen] = useState(false)
    const [search, setSearch] = useState("")
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
    const [editingAreaId, setEditingAreaId] = useState<number | null>(null)
    const [editingContent, setEditingContent] = useState<{ areaId: number; contentId: number; name: string } | null>(null)
    const [editingSummaryAreaId, setEditingSummaryAreaId] = useState<number | null>(null)
    const [historyTarget, setHistoryTarget] = useState<OperationalDashboardHistoryTarget | null>(null)
    const [updatingDiscussionAreaId, setUpdatingDiscussionAreaId] = useState<number | null>(null)
    const [updatingRatingAreaId, setUpdatingRatingAreaId] = useState<number | null>(null)
    const [updatingPriorityContentId, setUpdatingPriorityContentId] = useState<number | null>(null)

    useEffect(() => {
        if (!validId || !validMeetingId) return
        let active = true
        setIsLoading(true)
        setError(null)
        dispatch(loadOperationalDashboardOverview({ id: dashboardId, meetingId }))
            .then(unwrapResult)
            .then(result => {
                if (!active) return
                const key = `${dashboardId}:${meetingId ?? ""}`
                if (expandedFor.current !== key) {
                    setExpandedAreaIds(getOperationalDashboardOverviewSettings(result.dashboard.configJson).defaultAreaExpansion === "expanded"
                        ? new Set(result.areas.map(area => area.areaId)) : new Set())
                    expandedFor.current = key
                }
                setData(result)
            })
            .catch((reason: APIError) => {
                if (active) {
                    console.error(reason)
                    setError(reason)
                }
            })
            .finally(() => { if (active) setIsLoading(false) })
        return () => { active = false }
    }, [dispatch, dashboardId, meetingId, validId, validMeetingId, reload])

    useEffect(() => {
        const isOwner = !!loggedUser && data?.dashboard.ownerName === loggedUser
        if (!isOwner) {
            setEditMode(false)
            setEditingAreaId(null)
        }
        if (!isOwner || data?.meetingMinutesStatus?.trim().toUpperCase() !== "INMEETING") {
            setEditingContent(null)
            setEditingSummaryAreaId(null)
        }
    }, [data?.dashboard.ownerName, data?.meetingMinutesStatus, loggedUser])

    if (!validId || !validMeetingId) return <Alert severity="error">{label("invalid-id", "Nieprawidłowy identyfikator dashboardu lub spotkania.")}</Alert>
    if (isLoading) return <LoadingContainer />
    if (error) return (
        <Stack spacing={1}>
            <ErrorContainer error={error} />
            <Button onClick={() => setReload(value => value + 1)} sx={{ alignSelf: "flex-start" }}>{l10n.getString("retry", null, "_Ponów")}</Button>
        </Stack>
    )
    if (!data) return <Alert severity="info">{label("no-data", "Brak danych dashboardu.")}</Alert>

    const description = data.dashboard.description?.trim()
    const meetingStatus = data.meetingMinutesStatus?.trim().toUpperCase()
    const isInMeeting = meetingStatus === "INMEETING"
    const canEdit = !!loggedUser && data.dashboard.ownerName === loggedUser
    const canEditDuringMeeting = canEdit && isInMeeting
    const { showAgendaRating, showTopicStatus, showHeartbeat, linesInConsent } =
        getOperationalDashboardOverviewSettings(data.dashboard.configJson)
    const now = new Date()
    const query = search.trim().toLocaleLowerCase()
    const matches = (text: string) => text.toLocaleLowerCase().includes(query)
    const visibleAreas = [...data.areas]
        .sort((a, b) => a.order - b.order || a.areaId - b.areaId)
        .filter(area => {
            const text = [area.name, area.description, area.agendaName, area.responsibleName, area.meetingResponsibleName,
            area.summary, area.decision, ...area.contents.flatMap(content => [content.name, content.description]),
            ...area.items.map(topicSearchText)].join(" ")
            return matches(text)
        })
    const allVisibleExpanded = visibleAreas.length > 0 && visibleAreas.every(area => expandedAreaIds.has(area.areaId))
    const hasVisibleExpanded = visibleAreas.some(area => expandedAreaIds.has(area.areaId))
    const selectedArea = visibleAreas.find(area => area.areaId === selectedAreaId) ?? visibleAreas[0]
    const editingArea = data.areas.find(area => area.areaId === editingAreaId)
    const editingSummaryArea = data.areas.find(area => area.areaId === editingSummaryAreaId)
    const changeRating = async (area: OperationalDashboardAreaOverviewDto, rating: AreaRatingValue) => {
        if (!canEditDuringMeeting || area.meetingAgendaItemId == null || updatingRatingAreaId !== null) return
        setUpdatingRatingAreaId(area.areaId)
        try {
            const result = await dispatch(updateOperationalDashboardAreaAgendaRating({
                dashboardId, areaId: area.areaId, dto: { rating },
            }))
            if (updateOperationalDashboardAreaAgendaRating.fulfilled.match(result) && result.payload === true) {
                setData(current => current && current.dashboard.id === dashboardId && current.meetingId === data.meetingId
                    ? {
                        ...current,
                        areas: current.areas.map(item => item.areaId === area.areaId ? { ...item, rating } : item),
                    }
                    : current)
            } else {
                const failure = updateOperationalDashboardAreaAgendaRating.rejected.match(result) ? result.payload : undefined
                dispatch(showError(failure?.kind === "http" && failure.problem?.title
                    ? failure.problem.title : "operational-dashboard-rating-save-error"))
            }
        } catch (reason) {
            console.error(reason)
            dispatch(showError("operational-dashboard-rating-save-error"))
        } finally {
            setUpdatingRatingAreaId(null)
        }
    }
    const changePriority = async (area: OperationalDashboardAreaOverviewDto, content: OperationalDashboardAreaContentOverviewDto,
        priority: UpdateOperationalDashboardAreaContentPriorityRequest["priority"]) => {
        if (!canEditDuringMeeting || !isMeetingMinuteContent(content) || !content.item || updatingPriorityContentId !== null) return
        setUpdatingPriorityContentId(content.id)
        try {
            const result = await dispatch(updateOperationalDashboardAreaContentPriority({
                dashboardId, areaContentId: content.id, dto: { priority },
            }))
            if (updateOperationalDashboardAreaContentPriority.fulfilled.match(result) && result.payload === true) {
                setData(current => current && current.dashboard.id === dashboardId && current.meetingId === data.meetingId
                    ? {
                        ...current,
                        areas: current.areas.map(item => item.areaId === area.areaId
                            ? {
                                ...item,
                                contents: item.contents.map(entry => entry.id === content.id && entry.item
                                    ? { ...entry, item: { ...entry.item, priority } } : entry),
                            }
                            : item),
                    }
                    : current)
            } else {
                const failure = updateOperationalDashboardAreaContentPriority.rejected.match(result) ? result.payload : undefined
                dispatch(showError(failure?.kind === "http" && failure.problem?.title
                    ? failure.problem.title : "operational-dashboard-priority-save-error"))
            }
        } catch (reason) {
            console.error(reason)
            dispatch(showError("operational-dashboard-priority-save-error"))
        } finally {
            setUpdatingPriorityContentId(null)
        }
    }
    const toggleDiscussed = async (area: OperationalDashboardAreaOverviewDto) => {
        if (!showDiscussionStatus || !canEditDuringMeeting || area.meetingAgendaItemId == null || updatingDiscussionAreaId !== null) return
        const isDiscussed = area.discussionStatus?.toUpperCase() === "DISCUSSED"
        setUpdatingDiscussionAreaId(area.areaId)
        try {
            const configuration = await dispatch(loadOperationalDashboardAreaConfiguration({
                dashboardId, areaId: area.areaId,
            }))
            if (!loadOperationalDashboardAreaConfiguration.fulfilled.match(configuration)) {
                const failure = loadOperationalDashboardAreaConfiguration.rejected.match(configuration) ? configuration.payload : undefined
                dispatch(showError(failure?.kind === "http" && failure.problem?.title
                    ? failure.problem.title : "operational-dashboard-discussion-save-error"))
                return
            }
            const sharedGuid = configuration.payload.meetingAgendaItemSharedGuid
            if (!sharedGuid) {
                dispatch(showError("operational-dashboard-discussion-unavailable"))
                return
            }
            const result = await dispatch(updateOperationalDashboardAgendaDiscussionStatus({
                dashboardId, dto: {
                    meetingAgendaItemSharedGuid: sharedGuid,
                    discussionStatus: isDiscussed ? "NOT_DISCUSSED" : "DISCUSSED",
                },
            }))
            if (updateOperationalDashboardAgendaDiscussionStatus.fulfilled.match(result) && result.payload === true) {
                dispatch(showSuccess(isDiscussed
                    ? "operational-dashboard-discussion-unmarked" : "operational-dashboard-discussion-saved"))
                setReload(value => value + 1)
            } else {
                const failure = updateOperationalDashboardAgendaDiscussionStatus.rejected.match(result) ? result.payload : undefined
                dispatch(showError(failure?.kind === "http" && failure.problem?.title
                    ? failure.problem.title : "operational-dashboard-discussion-save-error"))
            }
        } catch (reason) {
            console.error(reason)
            dispatch(showError("operational-dashboard-discussion-save-error"))
        } finally {
            setUpdatingDiscussionAreaId(null)
        }
    }

    return (
        <Box sx={{
            width: "100%", minWidth: 0, bgcolor: "background.default", color: "text.primary",
            p: { xs: 1, md: 2 }, borderRadius: 3,
        }}>
            <Paper component="header" variant="outlined" sx={theme => ({
                bgcolor: "background.paper", backgroundImage: "none",
                borderColor: "divider",
                borderRadius: 3, boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.07)}`,
                mb: 2, p: { xs: 1.5, md: 2 },
            })}>
                <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between"
                    alignItems={{ xs: "flex-start", lg: "flex-start" }} gap={2}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography component="h1" variant="h4" sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 700, overflowWrap: "anywhere" }}>
                            {data.dashboard.name}
                        </Typography>
                        {description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: "72ch", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                                {description}
                            </Typography>
                        )}
                    </Box>
                    <OperationalDashboardMeetingPanel data={data} dashboardId={dashboardId}
                        showHeartbeat={showHeartbeat}
                        meetingStatus={meetingStatus} detailsOpen={meetingDetailsOpen}
                        onToggleDetails={() => setMeetingDetailsOpen(open => !open)}
                        onEndMeeting={() => setCloseMeetingDialogOpen(true)} />
                </Stack>
            </Paper>
            {!data.dashboard.isActive && <Alert severity="warning" sx={{ mb: 2 }}>{label("inactive", "Dashboard jest nieaktywny.")}</Alert>}
            {/* TODO: Re-enable OperationalDashboardMetrics when ready */}
            {/* <OperationalDashboardMetrics areas={data.areas} otherItems={data.otherItems} now={now} colorMode={colorMode} /> */}
            <Stack direction={{ xs: "column", md: "row" }} gap={1.5} sx={{
                mb: 2, p: 1.5, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2.5,
            }}>
                <TextField size="small" label={label("search", "Szukaj obszaru, tematu lub zadania")} value={search}
                    onChange={event => setSearch(event.target.value)} sx={{ flex: 1 }}
                    InputProps={{ endAdornment: search && (
                        <InputAdornment position="end">
                            <IconButton size="small" edge="end" onClick={() => setSearch("")}
                                aria-label={l10n.getString("clear-search", null, "_Wyczyść wyszukiwanie")}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) }} />
                <ToggleButtonGroup size="small" exclusive value={view}
                    onChange={(_, value: View | null) => { if (value) setView(value) }}
                    aria-label={label("layout", "Układ obszarów")}
                    sx={theme => ({
                        bgcolor: alpha(theme.palette.primary.main, 0.055), borderRadius: 1.5,
                        "& .MuiToggleButton-root": {
                            textTransform: "none", fontWeight: 600, px: 1.5,
                            color: "text.secondary", borderColor: "divider"
                        },
                        "& .MuiToggleButton-root.Mui-selected": {
                            bgcolor: "primary.main", color: "primary.contrastText",
                            "&:hover": { bgcolor: "primary.dark" }
                        },
                    })}>
                    <ToggleButton value="tiles" aria-label={l10n.getString("layout-tiles", null, "_Kafelki")} title={l10n.getString("layout-tiles", null, "_Kafelki")}>
                        <GridViewIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="list" aria-label={l10n.getString("layout-list", null, "_Lista")} title={l10n.getString("layout-list", null, "_Lista")}>
                        <ViewListIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                    <Tooltip title={l10n.getString("expand-all", null, "_Rozwiń wszystkie")}>
                        <span>
                            <IconButton size="small" color="primary"
                                aria-label={l10n.getString("expand-all", null, "_Rozwiń wszystkie")}
                                disabled={view !== "tiles" || !visibleAreas.length || allVisibleExpanded}
                                onClick={() => setExpandedAreaIds(current => {
                                    const next = new Set(current)
                                    visibleAreas.forEach(area => next.add(area.areaId))
                                    return next
                                })}>
                                <OpenInFullIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={l10n.getString("collapse-all", null, "_Zwiń wszystkie")}>
                        <span>
                            <IconButton size="small" color="primary"
                                aria-label={l10n.getString("collapse-all", null, "_Zwiń wszystkie")}
                                disabled={view !== "tiles" || !hasVisibleExpanded}
                                onClick={() => setExpandedAreaIds(current => {
                                    const next = new Set(current)
                                    visibleAreas.forEach(area => next.delete(area.areaId))
                                    return next
                                })}>
                                <CloseFullscreenIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
                {canEdit && (
                    <Tooltip title={l10n.getString("edit-mode", null, "_Tryb edycji")}>
                        <IconButton size="small" aria-label={l10n.getString("edit-mode", null, "_Tryb edycji")} aria-pressed={editMode && canEdit}
                            onClick={() => setEditMode(previous => !previous)}
                            sx={theme => ({
                                alignSelf: "center",
                                border: 1, borderColor: editMode && canEdit ? "primary.main" : "divider",
                                bgcolor: editMode && canEdit ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                                color: editMode && canEdit ? "primary.main" : "text.secondary",
                            })}>
                            <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={l10n.getString("refresh-data", null, "_Odśwież dane")}>
                    <IconButton
                        aria-label={l10n.getString("refresh-data", null, "_Odśwież dane")}
                        onClick={() => setReload(value => value + 1)}
                        sx={{ alignSelf: "center", color: "primary.main" }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
                <OverviewColorModeMenu value={colorMode} onChange={onColorModeChange} />
                <Tooltip title={label("prints", "Wydruki")}>
                    <IconButton aria-label={label("prints", "Wydruki")}
                        onClick={() => setPrintDialogOpen(true)}
                        sx={{ alignSelf: "center", color: "primary.main" }}>
                        <PrintOutlinedIcon />
                    </IconButton>
                </Tooltip>
            </Stack>
            {!data.areas.length ? (
                <Alert severity="info">{label("no-areas", "Dashboard nie ma jeszcze skonfigurowanych obszarów.")}</Alert>
            ) : !visibleAreas.length ? (
                <Alert severity="info" action={
                    <Button color="inherit" onClick={() => setSearch("")}>{l10n.getString("clear", null, "_Wyczyść")}</Button>
                }>{label("no-matches", "Brak obszarów pasujących do wyszukiwania.")}</Alert>
            ) : view === "list" ? (
                <Box sx={{
                    display: "grid", gap: 3, alignItems: "start",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.45fr) minmax(320px, 1fr)" },
                }}>
                    <Box component="section" aria-label={label("operational-areas", "Obszary operacyjne")} sx={{ minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                            <Typography component="h2" variant="h6" fontWeight={600}>
                                {label("operational-areas", "Obszary operacyjne")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" aria-live="polite">
                                {visibleAreas.length}/{data.areas.length} {label("areas-count", "obszarów")}
                                {showAgendaRating && <> · {visibleAreas.filter(area =>
                                    area.meetingAgendaItemId != null && area.rating?.toUpperCase() === "RED"
                                ).length} {label("critical-areas", "krytyczne")}</>}
                            </Typography>
                        </Stack>
                        <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2, bgcolor: "background.paper" }}>
                            {visibleAreas.map(area => (
                                <AreaListItem key={area.areaId} area={area}
                                    showAreaStatus={showAgendaRating}
                                    selected={area.areaId === selectedArea?.areaId} editMode={editMode && canEdit}
                                    hasMeeting={data.meetingId != null} inMeeting={isInMeeting} meetingMinutesId={data.meetingMinutesId}
                                    onSelect={() => setSelectedAreaId(area.areaId)}
                                    onEdit={() => { if (canEdit) setEditingAreaId(area.areaId) }}
                                    onChangeRating={canEditDuringMeeting ? rating => changeRating(area, rating) : undefined}
                                    ratingSaving={updatingRatingAreaId === area.areaId}
                                    ratingBusy={updatingRatingAreaId !== null} />
                            ))}
                        </Paper>
                    </Box>
                    {selectedArea && (
                        <AreaInspector key={selectedArea.areaId} area={selectedArea} hasMeeting={data.meetingId != null} inMeeting={isInMeeting}
                            showAreaStatus={showAgendaRating} showPriorityDots={showTopicStatus}
                            showDiscussionStatus={showDiscussionStatus}
                            meetingMinutesId={data.meetingMinutesId}
                            onViewContentHistory={content => setHistoryTarget({ kind: "content", contentId: content.id, name: content.name })}
                            onViewAgendaHistory={() => setHistoryTarget({ kind: "agenda", areaId: selectedArea.areaId, name: selectedArea.name })}
                            onEditSummary={canEditDuringMeeting ? () => setEditingSummaryAreaId(selectedArea.areaId) : undefined}
                            onToggleDiscussed={showDiscussionStatus && canEditDuringMeeting ? () => toggleDiscussed(selectedArea) : undefined}
                            discussing={updatingDiscussionAreaId === selectedArea.areaId}
                            discussionBusy={updatingDiscussionAreaId !== null}
                            onChangeRating={canEditDuringMeeting ? rating => changeRating(selectedArea, rating) : undefined}
                            ratingSaving={updatingRatingAreaId === selectedArea.areaId}
                            ratingBusy={updatingRatingAreaId !== null}
                            onEditContent={canEditDuringMeeting ? content =>
                                setEditingContent({ areaId: selectedArea.areaId, contentId: content.id, name: content.name }) : undefined}
                            onChangePriority={canEditDuringMeeting ? (content, priority) => changePriority(selectedArea, content, priority) : undefined}
                            priorityBusy={updatingPriorityContentId !== null} />
                    )}
                </Box>
            ) : (
                <Box sx={{
                    display: "grid", gap: 2, alignItems: "start",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
                }}>
                    {visibleAreas.map(area => (
                        <AreaPanel key={area.areaId} area={area}
                            showAreaStatus={showAgendaRating} showPriorityDots={showTopicStatus}
                            descriptionLines={linesInConsent}
                            expanded={expandedAreaIds.has(area.areaId)}
                            onToggleExpand={() => setExpandedAreaIds(current => {
                                const next = new Set(current)
                                if (next.has(area.areaId)) next.delete(area.areaId)
                                else next.add(area.areaId)
                                return next
                            })}
                            showDiscussionStatus={showDiscussionStatus}
                            hasMeeting={data.meetingId != null} inMeeting={isInMeeting} colorMode={colorMode} meetingMinutesId={data.meetingMinutesId}
                            editMode={editMode && canEdit} onEdit={() => { if (canEdit) setEditingAreaId(area.areaId) }}
                            onViewContentHistory={content => setHistoryTarget({ kind: "content", contentId: content.id, name: content.name })}
                            onViewAgendaHistory={() => setHistoryTarget({ kind: "agenda", areaId: area.areaId, name: area.name })}
                            onEditSummary={canEditDuringMeeting ? () => setEditingSummaryAreaId(area.areaId) : undefined}
                            onToggleDiscussed={showDiscussionStatus && canEditDuringMeeting ? () => toggleDiscussed(area) : undefined}
                            discussing={updatingDiscussionAreaId === area.areaId}
                            discussionBusy={updatingDiscussionAreaId !== null}
                            onChangeRating={canEditDuringMeeting ? rating => changeRating(area, rating) : undefined}
                            ratingSaving={updatingRatingAreaId === area.areaId}
                            ratingBusy={updatingRatingAreaId !== null}
                            onEditContent={canEditDuringMeeting ? content =>
                                setEditingContent({ areaId: area.areaId, contentId: content.id, name: content.name }) : undefined}
                            onChangePriority={canEditDuringMeeting ? (content, priority) => changePriority(area, content, priority) : undefined}
                            priorityBusy={updatingPriorityContentId !== null} />
                    ))}
                </Box>
            )}
            {canEdit && editingArea && <OperationalDashboardAreaDialog key={editingArea.areaId} dashboardId={dashboardId}
                areaId={editingArea.areaId} onClose={() => setEditingAreaId(null)}
                onSaved={() => { setEditingAreaId(null); setReload(value => value + 1) }} />}
            {canEditDuringMeeting && editingContent && <OperationalDashboardContentDialog key={`${editingContent.areaId}-${editingContent.contentId}`}
                dashboardId={dashboardId} areaId={editingContent.areaId}
                contentId={editingContent.contentId} contentName={editingContent.name}
                onClose={() => setEditingContent(null)}
                onSaved={() => { setEditingContent(null); setReload(value => value + 1) }} />}
            {canEditDuringMeeting && editingSummaryArea && <OperationalDashboardAgendaSummaryDialog
                key={editingSummaryArea.areaId} dashboardId={dashboardId} areaId={editingSummaryArea.areaId}
                areaName={editingSummaryArea.name} summary={editingSummaryArea.summary}
                onClose={() => setEditingSummaryAreaId(null)}
                onSaved={() => { setEditingSummaryAreaId(null); setReload(value => value + 1) }} />}
            {historyTarget && <OperationalDashboardHistoryDialog
                key={`${historyTarget.kind}-${historyTarget.kind === "content" ? historyTarget.contentId : historyTarget.areaId}`}
                target={historyTarget} dashboardId={dashboardId}
                onClose={() => setHistoryTarget(null)} />}
            {closeMeetingDialogOpen && data.meetingMinutesId?.trim() && (
                <CloseMeetingDialog meetingMinutesId={data.meetingMinutesId}
                    onClose={() => setCloseMeetingDialogOpen(false)}
                    onClosed={() => { setCloseMeetingDialogOpen(false); setReload(value => value + 1) }} />
            )}
            {printDialogOpen && (
                <ReportPrinter documentId={data.dashboard.guid} language={locale} mode="open"
                    reportPath={`api/reports/operational-report/${encodeURIComponent(data.dashboard.guid)}`}
                    isDialogOpen={printDialogOpen} closeDialog={() => setPrintDialogOpen(false)} />
            )}
        </Box>
    )
}

// Lokalny ThemeProvider zachowuje ustawienia aplikacji i nadpisuje tylko paletę dashboardu.
export const OperationalDashboardOverview = () => {
    const parentTheme = useTheme()
    const [colorMode, setColorMode] = useState<OverviewColorMode>("ocean")
    const overviewTheme = useMemo(() => createOverviewTheme(parentTheme, colorMode), [parentTheme, colorMode])
    return (
        <ThemeProvider theme={overviewTheme}>
            <OperationalDashboardOverviewContent colorMode={colorMode} onColorModeChange={setColorMode} />
        </ThemeProvider>
    )
}
